import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/generator/Header";
import { DesignConsole } from "@/components/generator/DesignConsole";
import { ConfigEditor } from "@/components/generator/ConfigEditor";
import { HistoryDialog } from "@/components/generator/HistoryDialog";
import { ProjectsDialog } from "@/components/generator/ProjectsDialog";
import { JobsDialog } from "@/components/generator/JobsDialog";
import { KbDialog } from "@/components/generator/KbDialog";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function Generator() {
  const [config, setConfig] = useState("");
  const [meta, setMeta] = useState(null);
  const [citations, setCitations] = useState([]);
  const [unverified, setUnverified] = useState([]);
  const [building, setBuilding] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [jobsOpen, setJobsOpen] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [activeCO, setActiveCO] = useState(null);
  const [kbOpen, setKbOpen] = useState(false);

  const build = async (payload) => {
    setBuilding(true);
    setStreaming(true);
    setConfig("");
    setCitations([]);
    setUnverified([]);
    setMeta({ vendors: payload.vendors });
    try {
      const { data } = await api.post("/build/start", payload);
      const jobId = data.job_id;
      const maxPolls = 350; // ~7 min safety cap
      let polls = 0;
      while (true) {
        if (++polls > maxPolls) {
          toast.error("Build timed out — try narrowing the scope");
          break;
        }
        await sleep(1200);
        let st;
        try {
          ({ data: st } = await api.get(`/generate/status/${jobId}`));
        } catch {
          continue;
        }
        if (st.config) setConfig(st.config);
        if (st.status === "error") {
          toast.error(st.error || "Build failed");
          break;
        }
        if (st.done) {
          setMeta(st);
          setCitations(st.citations || []);
          setUnverified(st.unverified || []);
          toast.success(`Built ${st.devices_count} device configuration(s)`);
          break;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Build failed");
    }
    setStreaming(false);
    setBuilding(false);
  };

  const loadFromHistory = (it) => {
    setMeta(it);
    setConfig(it.config);
    setCitations(it.citations || []);
    setUnverified(it.unverified || []);
    setStreaming(false);
  };

  return (
    <div className="h-screen flex flex-col bg-zinc-950 text-zinc-100 overflow-hidden">
      <Header onOpenHistory={() => setHistoryOpen(true)} onOpenKb={() => setKbOpen(true)} onOpenProjects={() => setProjectsOpen(true)} onOpenJobs={() => setJobsOpen(true)} />

      <div className="grid grid-cols-1 lg:grid-cols-12 flex-1 overflow-hidden">
        <div className="lg:col-span-4 border-r border-zinc-800 bg-zinc-950 flex flex-col overflow-hidden">
          <DesignConsole building={building} onBuild={build} activeJob={activeJob}
            activeCO={activeCO}
            onJobConsumed={() => setActiveJob(null)}
            onCOConsumed={() => setActiveCO(null)}
            onReset={() => { setConfig(""); setMeta(null); }} />
        </div>

        <div className="lg:col-span-8 overflow-hidden">
          <ConfigEditor
            config={config} meta={meta} loading={building} streaming={streaming}
            citations={citations} unverified={unverified}
          />
        </div>
      </div>

      <HistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} onLoad={loadFromHistory} />
      <ProjectsDialog open={projectsOpen} onOpenChange={setProjectsOpen} onLoad={loadFromHistory} />
      <JobsDialog open={jobsOpen} onOpenChange={setJobsOpen} onStartJob={(j) => setActiveJob(j)} onStartCO={(co) => setActiveCO(co)} onLoad={loadFromHistory} />
      <KbDialog open={kbOpen} onOpenChange={setKbOpen} />
    </div>
  );
}
